import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { create } from 'zustand';
import { layouts, RegionKind } from './layouts';

/** A region is bound to one slot, or to several rendered in order. */
export type Binding = Record<string, string | string[]>;

export interface LayoutParams {
  layout: string;
  bind: Binding;
}

/** The slot component the host gives this plugin, as `app.Extension`. */
type Extension = PiletApi['Extension'];

// This plugin's own state: which tab is active in each tabbed slot. A zustand
// store local to this plugin; no other plugin can reach it.
interface TabState {
  active: Record<string, string>;
  /** What a tab shows after its title, keyed by `slot/id`; set by the `layout.tabBadge` event. */
  badges: Record<string, { text: string | number; tone?: 'good' | 'bad' | 'level' } | undefined>;
  /** Regions hidden by `layout.tabsHidden` (the strip and the tabs' bodies), keyed by slot. */
  hidden: Record<string, boolean>;
  select(slot: string, id: string): void;
  setHidden(slot: string, hidden: boolean): void;
  badge(slot: string, id: string, badge: string | number | undefined, tone?: 'good' | 'bad' | 'level'): void;
}
export const useTabs = create<TabState>((set) => ({
  active: {},
  badges: {},
  hidden: {},
  select: (slot, id) => set((s) => ({ active: { ...s.active, [slot]: id } })),
  setHidden: (slot, hidden) => set((s) => (s.hidden[slot] === hidden ? s : { hidden: { ...s.hidden, [slot]: hidden } })),
  badge: (slot, id, badge, tone) =>
    set((s) => ({ badges: { ...s.badges, [`${slot}/${id}`]: badge === undefined ? undefined : { text: badge, tone } } })),
}));

export const Layout: React.FC<LayoutParams & { Extension: Extension }> = ({ layout, bind, Extension }) => {
  const definition = layouts[layout];
  if (!definition) {
    return <p className="layout-error">Unknown layout "{layout}".</p>;
  }
  return (
    <div className={`layout layout-${layout}`}>
      {Object.entries(definition.regions).map(([region, kind]) => (
        <div key={region} className={`region region-${region} region-kind-${kind}`}>
          <Region kind={kind} slots={toSlots(bind[region])} Extension={Extension} />
        </div>
      ))}
    </div>
  );
};

function toSlots(binding: string | string[] | undefined): string[] {
  if (!binding) return [];
  return Array.isArray(binding) ? binding : [binding];
}

const Region: React.FC<{ kind: RegionKind; slots: string[]; Extension: Extension }> = ({ kind, slots, Extension }) => {
  if (kind === 'tabs') {
    return <Tabs slot={slots[0]} Extension={Extension} />;
  }
  return (
    <>
      {slots.map((slot) => (
        <Extension key={slot} name={slot} order={byOrder} />
      ))}
    </>
  );
};

/** Contributions in any region render by their registration `order`, lowest first. */
const byOrder = <T extends { defaults?: { order?: number } }>(items: T[]): T[] =>
  [...items].sort((a, b) => (a.defaults?.order ?? 0) - (b.defaults?.order ?? 0));

/** What a tab contribution passes as its registration defaults. */
interface TabParams {
  id: string;
  title: string;
  order?: number;
}

// The tab strip is built from the contributions themselves: each one's
// registration defaults carry its id, title and order. Which tab is active
// lives in this plugin's store, keyed by slot.
/**
 * Called when the reader clicks a tab, as distinct from a tab selected by
 * `layout.selectTab`: index.tsx sets it to emit `layout.tabClick`, so a
 * plugin can act on the reader's choice (e.g. move the board to a stage).
 */
export let onTabClick: (slot: string, id: string) => void = () => {};
export function setTabClick(handler: (slot: string, id: string) => void): void {
  onTabClick = handler;
}

const Tabs: React.FC<{ slot?: string; Extension: Extension }> = ({ slot, Extension }) => {
  const active = useTabs((s) => (slot ? s.active[slot] : undefined));
  const select = useTabs((s) => s.select);
  const badges = useTabs((s) => s.badges);
  const hidden = useTabs((s) => (slot ? !!s.hidden[slot] : false));
  if (!slot) return null;
  return (
    <Extension
      name={slot}
      order={byOrder}
      render={(children: React.ReactNode) => {
        const elements = React.Children.toArray(children) as React.ReactElement[];
        const tabs = elements
          .map((el) => el.props?.params as TabParams | undefined)
          .filter((p): p is TabParams => !!p && typeof p.id === 'string');
        const current = tabs.some((t) => t.id === active) ? active : tabs[0]?.id;
        return (
          <div className={`tabs${hidden ? ' tabs-hidden' : ''}`} hidden={hidden}>
            <nav className="tab-strip">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`tab${t.id === current ? ' tab-active' : ''}`}
                  onClick={() => {
                    select(slot, t.id);
                    onTabClick(slot, t.id);
                  }}>
                  {t.title}
                  {(() => {
                    const b = badges[`${slot}/${t.id}`];
                    return b ? <span className={`tab-badge${b.tone ? ` tab-badge-${b.tone}` : ''}`}>{b.text}</span> : null;
                  })()}
                </button>
              ))}
            </nav>
            <div className="tab-body">{elements.filter((el) => el.props?.params?.id === current)}</div>
          </div>
        );
      }}
    />
  );
};
